import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Stack
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Casino as GenerateIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { BRAND } from '../../constants/branding';

const ADMIN_PASSWORD_LENGTH = 64;

const generateStrongClientPassword = (length = ADMIN_PASSWORD_LENGTH) => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const digits = '23456789';
  const symbols = '!@#$%^&*-_=+?';
  const all = upper + lower + digits + symbols;
  const picks = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)]
  ];
  while (picks.length < length) {
    picks.push(all[Math.floor(Math.random() * all.length)]);
  }
  for (let i = picks.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [picks[i], picks[j]] = [picks[j], picks[i]];
  }
  return picks.join('');
};

const isStrongAdminPassword = (value) => {
  const v = String(value || '');
  return (
    v.length >= ADMIN_PASSWORD_LENGTH
    && /[A-Z]/.test(v)
    && /[a-z]/.test(v)
    && /[0-9]/.test(v)
    && /[!@#$%^&*\-_=+?]/.test(v)
  );
};

const ChangePasswordRequired = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = () => {
    const next = generateStrongClientPassword();
    setNewPassword(next);
    setConfirmPassword(next);
    setShowPassword(true);
    toast.info('64-character strong password generated — copy it before continuing');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isStrongAdminPassword(newPassword)) {
      setError(
        `New password must be at least ${ADMIN_PASSWORD_LENGTH} characters and include uppercase, lowercase, a number, and a symbol`
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/change-password', { newPassword });
      const profile = response.data?.data;
      if (profile) {
        localStorage.setItem('superadmin_user', JSON.stringify(profile));
      }
      const refreshed = await refreshProfile();
      if (refreshed) {
        localStorage.setItem('superadmin_user', JSON.stringify(refreshed));
      }
      toast.success('Password updated. You can now use the application.');
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        px: 2
      }}
    >
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Change Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {user?.email ? (
              <>
                A strong
                {' '}
                {ADMIN_PASSWORD_LENGTH}
                -character password is required for
                {' '}
                <strong>{user.email}</strong>
                {' '}
                before you can access
                {' '}
                {BRAND.productName}
                .
              </>
            ) : (
              <>You must set a strong {ADMIN_PASSWORD_LENGTH}-character password before continuing.</>
            )}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                type="button"
                variant="outlined"
                startIcon={<GenerateIcon />}
                onClick={handleGenerate}
                fullWidth
              >
                Generate strong password
              </Button>
            </Stack>
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              helperText={`${ADMIN_PASSWORD_LENGTH}+ chars with upper, lower, number, and symbol (${newPassword.length} entered)`}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Saving…' : 'Save password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChangePasswordRequired;
